
import Vue from 'vue';
import { functions } from '@/firebase';
import { mapActions, mapState, mapGetters } from 'vuex';
import DialogContent from '@/components/base/DialogContent.vue';
import DialogOrBottomSheet from '@/components/base/DialogOrBottomSheet.vue';
import { SNACKBAR_STATUS } from '@/constants/constants';
import { TeamUserRoleDef, TeamMember } from '@run-diffusion/shared';
import TeamMemberTeamRoleTableCell from '@/views/Team/TeamDashboard/TeamMemberTeamRoleTableCell.vue';
import RoleDefBadge from '@/views/Team/CompanyDashboard/RoleDefBadge.vue';
import ActionsIsland from '@/components/ActionsIsland.vue';
import EmptyState from '@/components/states/EmptyState.vue';
import { db } from '@/firebase';
import { get$bindFirestoreOptions } from '@/mixins/VuexFirestoreBindingsMixin';
import pluralize from 'pluralize';
import { TeamUserMixin } from '@/mixins';

export default Vue.extend({
	name: 'EditTeamMembers',
	mixins: [
		TeamUserMixin,
	],
	props: {
		value: { type: Boolean, default: false },
		currTeamMembers: { type: Array, default: () => [] },
		currTeam: { type: Object, default: null },
	},
	data () {
		const DEFAULT_PAGINATION = {
			sortBy: ['email'],
			sortDesc: [false],
		};
		return {
			saving: false,
			initing: false,

			// FORM
			formValid: true,

			// Global toggles
			allTeamsToggle: true,
			sendEmailToggle: true,

			// New Roles
			availableRoleDefs: [] as TeamUserRoleDef[],
			loadingAvailableRoleDefs: false,
			selectedRoleDefId: null as string | null,

			// Update status tracking
			memberUpdateStatus: {} as Record<string, { loading: boolean; success?: boolean }>,

			// Table config
			itemsPerPageOptions: [25, 100],
			DEFAULT_PAGINATION,
			pagination: DEFAULT_PAGINATION,
		};
	},
	watch: {
		async value (newVal: boolean) {
			if (newVal) await this.init();
		},
	},
	computed: {
		...mapState([
			'team',
			'user',
			'authUser',
		]),
		...mapGetters([
			'displayLabels',
			'companyClubOfferBenefits',
		]),
		headers () {
			return [
				{
					text: 'Member Email',
					value: 'email',
					align: 'left',
					class: 'py-0 pl-4 pr-1',
					sortable: true,
				},
				{
					text: `Current ${this.displayLabels.team} Role`,
					value: 'currentRoleLabel',
					align: 'left',
					class: 'py-0 px-1',
					sortable: false,
					noDownload: true,
				},
				{
					text: '',
					value: 'processing',
					align: 'left',
					class: 'py-0 px-1',
					sortable: false,
					noDownload: true,
				},
			];
		},

		newRoleDef (): TeamUserRoleDef | null {
			return (this.availableRoleDefs || []).find((r: TeamUserRoleDef) => r.id === this.selectedRoleDefId) || null;
		},
	},
	methods: {
		...mapActions([
			'updateSnackbar',
			'setUpsellDialog',
		]),
		onTeamRolesUpsell () {
			this.setUpsellDialog({ noTeamRoles: true });
		},
		async init () {
			this.initing = true;
			try {
				this.selectedRoleDefId = null;
				this.allTeamsToggle = true;
				this.sendEmailToggle = true;
				await this.bindTeamUserRoleDefs();
			} catch (e) {
				// non-fatal
			} finally {
				this.initing = false;
			}
		},
		async bindTeamUserRoleDefs () {
			this.loadingAvailableRoleDefs = true;
			try {
				const companyId = this.currTeam?.companyId || this.team?.companyId;
				if (!companyId) return;

				// index created: teamUserRoleDefs: companyId Ascending isDeleted Ascending level Descending __name__ Descending
				const roleDefsRef = db
					.collection('teamUserRoleDefs')
					.where('companyId', '==', companyId)
					.where('isDeleted', '==', false)
					.orderBy('level', 'desc');

				await this.$bind(
					'availableRoleDefs',
					roleDefsRef,
					get$bindFirestoreOptions(),
				);

				// Set a default selection if none chosen yet
				if (this.availableRoleDefs?.length && !this.selectedRoleDefId) {
					this.selectedRoleDefId = this.availableRoleDefs[0].id;
				}
			} catch (e) {
				console.error('Error binding team user role defs:', e);
			} finally {
				this.loadingAvailableRoleDefs = false;
			}
		},
		onResetAndClose () {
			this.memberUpdateStatus = {};
			this.$emit('input', false);
		},
		onDialogInput (value: boolean) {
			if (!value) {
				this.onResetAndClose();
			}
		},
		async onSave () {
			if (!this.currTeamMembers || !this.currTeamMembers.length || !this.currTeam) return;
			if (!this.selectedRoleDefId) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Please select a role',
					show: true,
				});
				return;
			}

			if (!this.$refs.form.validate()) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Form invalid, check fields',
					show: true,
				});
				return;
			}

			this.saving = true;

			// Initialize per-row update state to loading
			this.memberUpdateStatus = {};
			this.currTeamMembers.forEach((member: TeamMember) => {
				this.$set(this.memberUpdateStatus, member.email, { loading: true });
			});
			const onError: Function = (e: any) => {
				console.error('Error updating Team Members: ', e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Error updating members',
					show: true,
				});
			};

			try {
				const updatePromises = this.currTeamMembers.map(async (member: TeamMember) => {
					const functionRef = functions.httpsCallable('manageTeamUserRole');
					try {
						const { success } = (await functionRef({
							teamId: this.currTeam.id,
							email: member.email,
							teamUserRoleDefId: this.selectedRoleDefId,
							sendEmailNotification: this.sendEmailToggle,
							updateAllTeams: this.allTeamsToggle,
						})).data;
						this.$set(this.memberUpdateStatus, member.email, { loading: false, success: !!success });
						return { success: !!success, email: member.email };
					} catch (err) {
						console.error('Error updating member', member?.email, err);
						this.$set(this.memberUpdateStatus, member.email, { loading: false, success: false });
						return { success: false, email: member.email };
					}
				});

				const results: any[] = await Promise.all(updatePromises);
				const failedUpdates = results.filter(r => !r.success);

				if (failedUpdates.length > 0) {
					onError(new Error(`Failed to update ${failedUpdates.length} members`));
				} else {
					this.updateSnackbar({
						status: SNACKBAR_STATUS.SUCCESS,
						message: `Success! Updated ${this.currTeamMembers.length} ${pluralize('member', this.currTeamMembers.length)}`,
						show: true,
					});
					this.$emit('success');
					this.onResetAndClose();
				}
			} catch (e) {
				console.error(e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Error updating members, please try again',
					show: true,
				});
			} finally {
				this.saving = false;
			}
		},
	},
	components: {
		DialogContent,
		DialogOrBottomSheet,
		ActionsIsland,
		EmptyState,
		TeamMemberTeamRoleTableCell,
		RoleDefBadge,
	},
});
